import { motion, AnimatePresence } from 'framer-motion';
import { usePerfume } from '../context/PerfumeContext';
import { lightenColor, darkenColor } from '../utils/colorMixing';

export const Bottle = () => {
  const { fillPercent, getMixedColor, animatingDrop } = usePerfume();
  
  const mixedColor = getMixedColor();
  const isEmpty = fillPercent === 0;

  // Calculate liquid height position (inverted for SVG coordinate system)
  // The bottle body spans roughly from y=120 to y=380
  const liquidMaxHeight = 260;
  const liquidOffset = liquidMaxHeight * (1 - fillPercent / 100);

  return (
    <div className="relative flex items-center justify-center">
      {/* Drop Animation */}
      <AnimatePresence>
        {animatingDrop && (
          <motion.div
            key={animatingDrop.id}
            className="absolute z-10 w-4 h-6 rounded-b-full"
            style={{
              background: `linear-gradient(180deg, ${lightenColor(animatingDrop.color)} 0%, ${animatingDrop.color} 100%)`,
              top: -20,
              left: '50%',
              marginLeft: -8,
              boxShadow: `0 4px 12px ${animatingDrop.color}40`
            }}
            initial={{ y: 0, scale: 1, opacity: 1 }}
            animate={{ 
              y: [0, 300 + (liquidOffset * 0.7)],
              scale: [1, 0.8, 0.6],
              opacity: [1, 1, 0]
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 0.8,
              ease: [0.45, 0.05, 0.55, 0.95],
              times: [0, 0.8, 1]
            }}
          />
        )}
      </AnimatePresence>

      {/* SVG Bottle */}
      <svg 
        viewBox="0 0 200 400" 
        className="w-40 h-80 md:w-56 md:h-[420px] lg:w-64 lg:h-[480px]"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Glass gradient */}
          <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(200, 30%, 95%)" stopOpacity="0.9" />
            <stop offset="30%" stopColor="hsl(0, 0%, 100%)" stopOpacity="0.4" />
            <stop offset="70%" stopColor="hsl(200, 20%, 90%)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="hsl(200, 30%, 95%)" stopOpacity="0.9" />
          </linearGradient>

          {/* Liquid gradient (dynamic based on mixed color) */}
          {mixedColor && (
            <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={lightenColor(mixedColor, 10)} stopOpacity="0.95" />
              <stop offset="50%" stopColor={mixedColor} stopOpacity="0.9" />
              <stop offset="100%" stopColor={darkenColor(mixedColor, 10)} stopOpacity="0.95" />
            </linearGradient>
          )}

          {/* Glass highlight */}
          <linearGradient id="glassHighlight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(0, 0%, 100%)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="hsl(0, 0%, 100%)" stopOpacity="0" />
          </linearGradient>

          {/* Clip path for liquid - Aesop style rounded flask */}
          <clipPath id="bottleClip">
            <path
              d="M85 60
                 L85 100
                 C55 120 40 170 40 260
                 C40 340 60 380 100 380
                 C140 380 160 340 160 260
                 C160 170 145 120 115 100
                 L115 60
                 Z"
            />
          </clipPath>

          {/* Shadow filter */}
          <filter id="bottleShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="8" floodOpacity="0.1" />
          </filter>
        </defs>

        {/* Bottle shadow */}
        <ellipse 
          cx="100" 
          cy="385" 
          rx="45" 
          ry="8" 
          fill="hsl(0, 0%, 0%)" 
          opacity="0.08"
        />

        {/* Main bottle body - Elegant rounded flask shape */}
        <path
          d="M85 60
             L85 100
             C55 120 40 170 40 260
             C40 340 60 380 100 380
             C140 380 160 340 160 260
             C160 170 145 120 115 100
             L115 60
             Z"
          fill="url(#glassGradient)"
          stroke="hsl(0, 0%, 70%)"
          strokeWidth="1"
          filter="url(#bottleShadow)"
        />

        {/* Liquid inside bottle */}
        <g clipPath="url(#bottleClip)">
          <AnimatePresence>
            {!isEmpty && (
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Main liquid body */}
                <motion.rect
                  x="35"
                  y={120 + liquidOffset}
                  width="130"
                  height={liquidMaxHeight - liquidOffset + 20}
                  fill="url(#liquidGradient)"
                  initial={{ height: 0 }}
                  animate={{ 
                    y: 120 + liquidOffset,
                    height: liquidMaxHeight - liquidOffset + 20
                  }}
                  transition={{ 
                    type: 'spring',
                    stiffness: 100,
                    damping: 15
                  }}
                />

                {/* Animated wave on top */}
                <motion.path
                  d={`
                    M 0 ${120 + liquidOffset}
                    Q 50 ${115 + liquidOffset} 100 ${120 + liquidOffset}
                    T 200 ${120 + liquidOffset}
                    T 300 ${120 + liquidOffset}
                    L 300 ${400}
                    L 0 ${400}
                    Z
                  `}
                  fill={mixedColor}
                  opacity="0.5"
                  animate={{ x: [-100, 0, -100] }}
                  transition={{
                    duration: 4,
                    ease: 'easeInOut',
                    repeat: Infinity
                  }}
                />
              </motion.g>
            )}
          </AnimatePresence>
        </g>

        {/* Glass highlight (left side) */}
        <path
          d="M60 140
             C50 180 48 240 55 300
             C48 240 50 180 60 140
             Z"
          fill="url(#glassHighlight)"
          opacity="0.7"
        />

        {/* Glass highlight (small) */}
        <ellipse
          cx="70"
          cy="160"
          rx="8"
          ry="15"
          fill="hsl(0, 0%, 100%)"
          opacity="0.4"
        />

        {/* Bottle neck */}
        <rect
          x="85"
          y="35"
          width="30"
          height="30"
          rx="2"
          fill="url(#glassGradient)"
          stroke="hsl(0, 0%, 70%)"
          strokeWidth="1"
        />

        {/* Bottle cap/stopper */}
        <rect
          x="82"
          y="15"
          width="36"
          height="22"
          rx="3"
          fill="hsl(0, 0%, 15%)"
          stroke="hsl(0, 0%, 25%)"
          strokeWidth="0.5"
        />

        {/* Cap highlight */}
        <rect
          x="84"
          y="17"
          width="10"
          height="3"
          rx="1"
          fill="hsl(0, 0%, 30%)"
          opacity="0.5"
        />
      </svg>
    </div>
  );
};
